/** The week as the portal reads it: who was given what, what was done, and the rest of the plan. */
import { addDays } from "./days.js";
import { allSkipped, isLoggedOnly, keyOf, learnKeys, liftBlocks, norm, ownBlocks, planTitle, recordOf, skippedCount, skippedWorkout, working } from "./plan.js";

export { skippedWorkout };
import { isRunning } from "./running.js";
import { coachSessions, rowsGiven, sessionRecords, skippedBy, waiting, type CoachSession, type Published } from "./sessions.js";
import { setText, topSet } from "./sets.js";
import { columnFor, exerciseKey, mine, weekColumns } from "./text.js";
import type { Block, PlanRow, PublishedWeek, SetLog, TeamSessions, TeamWeek, Workout } from "./types.js";

/** `skipped`: done, every log of it skipped (§61.8); never Done, never Not logged. */
export type State = "done" | "skipped" | "missed" | "today" | "planned";

export interface Given {
  id: string;
  date: string;
  title: string;
  row: PlanRow;
  done: Workout[];
  state: State;
  /** Sets of it the athlete skipped, in the workouts that did it. */
  skipped: number;
}

/** One part of the rest of the plan, as one athlete has it. */
export interface AthleteTrack {
  key: string;
  date: string;
  label: string;
  lines: string[];
  logged: boolean;
  before: boolean;
  /** A run (§19.5): owed like a weights session, so it has a state. */
  running: boolean;
  state: State | null;
  /** Their workouts that day that logged it. */
  done: Workout[];
  /** Its sets they skipped; with nothing else logged, the part reads Skipped. */
  skipped: number;
}

export interface Athlete {
  id: string;
  name: string;
  prs: number;
  lastWorkout: string | null;
  groups: string[];
  given: Given[];
  workouts: Workout[];
  track: AthleteTrack[];
  extras: Workout[];
  /** Weights sessions given and running sessions in their columns: the m of "n of m". */
  total: number;
  done: number;
  /** Given sessions and runs every set of which they skipped: in neither `done` nor `missed`. */
  skipped: number;
  owed: number;
  missed: number;
  column: string | null;
  noGroup: boolean;
  /** A week the builder published left them out (§17.8). */
  nothing: boolean;
}

/** One part of the rest of the plan, across the team. */
export interface WeekTrack {
  key: string;
  date: string;
  label: string;
  lines: string[];
  columns: string[];
  before: boolean;
  running: boolean;
  athletes: Athlete[];
}

export interface WeekModel {
  monday: string;
  sunday: string;
  days: string[];
  today: string;
  published: boolean;
  publishedAt: string | null;
  rows: PlanRow[];
  planColumns: string[];
  personalColumns: string[];
  athletes: Athlete[];
  workouts: Workout[];
  titles: { [id: string]: string };
  sessionsCount: number;
  coach: CoachSession[];
  track: WeekTrack[];
  document: { week: string } | null;
}

export interface WeekInput {
  week?: TeamWeek | null;
  sessions?: TeamSessions | null;
  thisWeek?: PublishedWeek | null;
  prevWeek?: PublishedWeek | null;
  publishedAt?: string | null;
}

function stateOf(done: boolean, date: string, today: string, logs: readonly SetLog[] = []): State {
  return done ? (allSkipped(logs) ? "skipped" : "done") : date < today ? "missed" : date === today ? "today" : "planned";
}

/** The logs of these workouts that are of exercises in the blocks `which` picks of each. */
function logsIn(workouts: readonly Workout[], which: (w: Workout) => Block[]): SetLog[] {
  return workouts.flatMap((w) => {
    const ids = new Set(which(w).flatMap((b) => (b.exercises ?? []).map((e) => norm(e.id))));
    return (w.session.logs ?? []).filter((l) => ids.has(norm(l.exerciseID)));
  });
}



const byDate = <T extends { date: string }>(x: T, y: T) => (x.date < y.date ? -1 : x.date > y.date ? 1 : 0);

export function trackKey(date: string, label: string): string {
  return date + "~" + label;
}

function logs(w: Workout, label: string): Block[] {
  return (w.session.blocks ?? []).filter((b) => b.loggedFrom && String(b.loggedFrom).trim().toLowerCase() === label);
}

/** The blocks of their workouts that day logged from this part of the plan. */
export function loggedBlocks(t: Pick<AthleteTrack, "label" | "done">): { workout: Workout; block: Block }[] {
  const label = String(t.label || "").trim().toLowerCase();
  return t.done.flatMap((workout) => logs(workout, label).map((block) => ({ workout, block })));
}

// The rest of the plan: what is not lifted is still the athlete's training. A run is owed like a
// weights session (§19.5); anything else has no Done, and one the athlete logged says so. A part
// rides on the day's first weights workout (§61.4), so it is done only where a set of it was logged.
export function trackFor(rows: PlanRow[], theirs: Workout[], today: string, runsOwedFrom?: string | null): AthleteTrack[] {
  const seen = new Set<string>();
  const out: AthleteTrack[] = [];
  for (const r of rows) {
    for (const x of r.plan.alsoToday ?? []) {
      const key = trackKey(r.date, x.label);
      if (seen.has(key)) continue;
      seen.add(key);
      const label = String(x.label || "").trim().toLowerCase();
      const partLogs = (w: Workout) => logsIn([w], (v) => logs(v, label));
      const done = theirs.filter((w) => w.date === r.date && partLogs(w).length > 0);
      const doneLogs = done.flatMap(partLogs);
      const lines = x.lines ?? [];
      const running = !!runsOwedFrom && r.date >= runsOwedFrom && isRunPart(x);
      out.push({
        key,
        date: r.date,
        label: x.label,
        lines,
        logged: done.length > 0 && !allSkipped(doneLogs),
        before: !!x.isBeforeLifts,
        running,
        state: running ? stateOf(done.length > 0, r.date, today, doneLogs) : null,
        done,
        skipped: skippedCount(doneLogs),
      });
    }
  }
  return out.sort(byDate);
}

/** The reader's verdict (§61.4); a row published before it is judged by its lines. */
function isRunPart(x: { isRun?: boolean; lines?: string[] }): boolean {
  return typeof x.isRun === "boolean" ? x.isRun : isRunning(x.lines ?? []);
}

export function buildWeek(monday: string, input: WeekInput, today: string): WeekModel {
  const sunday = addDays(monday, 6);
  const days: string[] = [];
  for (let i = 0; i < 7; i++) days.push(addDays(monday, i));
  const inWeek = (date: string) => date >= monday && date <= sunday;
  const workouts = input.sessions?.workouts ?? [];
  for (const w of workouts) learnKeys(w.session);

  const published: Published[] = [];
  for (const [w, key] of [
    [input.thisWeek, monday],
    [input.prevWeek, addDays(monday, -7)],
  ] as const) {
    if (!w || !Array.isArray(w.sessions) || !w.sessions.length) continue;
    const rows = w.sessions.map((r) => ({ ...r, week: r.week || key }));
    if (key !== monday && !rows.some((r) => inWeek(r.date))) continue;
    published.push({ week: key, columns: w.columns ?? [], sessions: rows, athletes: w.athletes ?? [], document: w.document ?? null });
  }

  const sheets: { [week: string]: string[] } = {};
  for (const w of published) sheets[w.week] = w.columns;
  const rows = published.flatMap((w) => w.sessions);
  const weekRows = rows.filter((r) => inWeek(r.date));

  const personal: string[] = [];
  for (const w of published) for (const c of weekColumns(w.columns, w.sessions)) if (!personal.includes(c)) personal.push(c);
  const planColumns = personal.slice();
  if (weekRows.some((r) => r.group === "")) planColumns.push("");

  const groupsById = new Map<string, string[]>();
  for (const w of published) for (const a of w.athletes) if (!groupsById.has(a.id)) groupsById.set(a.id, a.groups ?? []);

  const athletes = (input.week?.athletes ?? []).map((a): Athlete => {
    const groups = groupsById.get(a.id) ?? [];
    const theirRows = rowsGiven(published, a.id, groups);
    const seen = new Set<string>();
    const given = theirRows
      .filter((r) => {
        const id = norm(r.sessionID);
        if (!inWeek(r.date) || !liftBlocks(r.plan?.blocks).length || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map((r): Given => {
        const id = norm(r.sessionID);
        const done = workouts.filter((w) => w.athleteID === a.id && norm(w.session.teamSessionID) === id);
        const own = logsIn(done, ownBlocks);
        return { id, date: r.date, title: planTitle(r.plan), row: r, done, state: stateOf(done.length > 0, r.date, today, own), skipped: skippedCount(own) };
      })
      .sort(byDate);
    const givenIDs = new Set(given.map((g) => g.id));
    const theirs = workouts.filter((w) => w.athleteID === a.id);
    const track = trackFor(
      theirRows.filter((r) => inWeek(r.date)),
      theirs,
      today,
      input.thisWeek?.runsOwedFrom,
    );
    const runs = track.filter((t) => t.running);
    let column: string | null = null;
    let noGroup = false;
    let nothing = false;
    for (const w of published) {
      if (column) break;
      const assigned = w.athletes.find((x) => x.id === a.id)?.assigned;
      if (assigned !== undefined) {
        if (assigned) column = assigned;
        else if (assigned === null) nothing = true;
        continue;
      }
      const cols = weekColumns(w.columns, w.sessions);
      const c = columnFor(cols, groups);
      if (c) column = c;
      else if (cols.length) noGroup = true;
    }
    return {
      id: a.id,
      name: a.name,
      prs: a.prs || 0,
      lastWorkout: a.lastWorkout || null,
      groups,
      given,
      workouts: theirs,
      track,
      extras: theirs.filter((w) => !givenIDs.has(norm(w.session.teamSessionID)) && !isLoggedOnly(w.session.blocks)),
      total: given.length + runs.length,
      done: given.filter((g) => g.state === "done").length + runs.filter((t) => t.state === "done").length,
      skipped: given.filter((g) => g.state === "skipped").length + runs.filter((t) => t.state === "skipped").length,
      owed: given.filter((g) => g.done.length || g.date <= today).length + runs.filter((t) => t.done.length || t.date <= today).length,
      missed: given.filter((g) => g.state === "missed").length + runs.filter((t) => t.state === "missed").length,
      column,
      noGroup: !column && noGroup,
      nothing: !column && nothing,
    };
  });

  const titles: { [id: string]: string } = {};
  for (const r of rows) titles[norm(r.sessionID)] = planTitle(r.plan);

  const trackByKey = new Map<string, WeekTrack>();
  const track: WeekTrack[] = [];
  for (const r of weekRows) {
    for (const x of r.plan.alsoToday ?? []) {
      const key = trackKey(r.date, x.label);
      let t = trackByKey.get(key);
      if (!t) {
        t = { key, date: r.date, label: x.label, lines: x.lines ?? [], columns: [], before: !!x.isBeforeLifts, running: false, athletes: [] };
        trackByKey.set(key, t);
        track.push(t);
      }
      if (!t.columns.includes(r.group)) t.columns.push(r.group);
      if ((x.lines ?? []).length > t.lines.length) t.lines = x.lines ?? [];
      const owed = input.thisWeek?.runsOwedFrom;
      if (owed && r.date >= owed && isRunPart(x)) t.running = true;
    }
  }
  track.sort(byDate);
  for (const t of track) t.athletes = athletes.filter((a) => a.track.some((x) => x.key === t.key));
  const sessionsCount = new Set(weekRows.filter((r) => liftBlocks(r.plan?.blocks).length).map((r) => norm(r.sessionID))).size;

  let document: { week: string } | null = null;
  const own = published.find((w) => w.week === monday);
  if (own?.document) document = { week: monday };
  else for (const w of published) if (!document && w.document) document = { week: w.week };

  return {
    monday,
    sunday,
    days,
    today,
    published: published.length > 0,
    publishedAt: input.publishedAt ?? null,
    rows: weekRows,
    planColumns,
    personalColumns: personal,
    athletes,
    workouts,
    titles,
    sessionsCount,
    coach: coachSessions(workouts, published, [monday, sunday]),
    track,
    document,
  };
}

// MARK: Reading the week.

export interface WeekItem {
  day: string;
  rank: number;
  i: number;
  cs?: CoachSession;
  track?: WeekTrack;
}

/** Every session of the week in order: the weights sessions and the rest of the plan. */
export function weekItems(model: Pick<WeekModel, "coach" | "track">): WeekItem[] {
  const items: Omit<WeekItem, "i">[] = [
    ...model.coach.map((cs) => ({ day: cs.day, rank: 1, cs })),
    ...model.track.map((t) => ({ day: t.date, rank: t.before ? 0 : 2, track: t })),
  ];
  return items
    .map((it, i) => ({ ...it, i }))
    .sort((a, b) => (a.day !== b.day ? (a.day < b.day ? -1 : 1) : a.rank !== b.rank ? a.rank - b.rank : a.i - b.i));
}

export interface TrainingDay {
  date: string;
  items: WeekItem[];
}

/** A training day is one session: its weights and the rest of the plan, every column's parts. */
export function weekDays(model: Pick<WeekModel, "coach" | "track">): TrainingDay[] {
  const days: TrainingDay[] = [];
  for (const it of weekItems(model)) {
    let d = days[days.length - 1];
    if (!d || d.date !== it.day) {
      d = { date: it.day, items: [] };
      days.push(d);
    }
    d.items.push(it);
  }
  return days;
}

export function partName(it: WeekItem): string {
  if (it.cs) return it.cs.plan ? planTitle(it.cs.plan) : "Workout";
  return it.track?.label ?? "";
}

export interface DayCounts {
  weights: boolean;
  running: boolean;
  given: number;
  done: number;
  /** Counted apart from `done` (§61.8). */
  skipped: number;
  records: number;
}


/** A day's owed sessions, weights and runs, across the athletes given them. */
export function dayCounts(day: TrainingDay): DayCounts {
  let given = 0;
  let done = 0;
  let skipped = 0;
  let records = 0;
  let weights = false;
  let running = false;
  for (const it of day.items) {
    if (it.track?.running) {
      running = true;
      const t = it.track;
      const theirs = t.athletes.map((a) => a.track.find((x) => x.key === t.key)).filter((x) => x?.running);
      given += theirs.length;
      done += theirs.filter((x) => x!.state === "done").length;
      skipped += theirs.filter((x) => x!.state === "skipped").length;
      continue;
    }
    if (!it.cs) continue;
    weights = true;
    const cs = it.cs;
    const skippedHere = [...skippedBy(cs)];
    if (cs.teamSessionID) {
      const givenIDs = new Set(cs.given.map((a) => a.id));
      const gone = skippedHere.filter((id) => givenIDs.has(id)).length;
      given += cs.given.length;
      done += cs.given.length - waiting(cs).length - gone;
      skipped += gone;
    } else {
      given += cs.workouts.length;
      done += cs.workouts.length - cs.workouts.filter(skippedWorkout).length;
      skipped += cs.workouts.filter(skippedWorkout).length;
    }
    records += sessionRecords(it.cs);
  }
  return { weights, running, given: given, done, skipped, records };
}

export function dayCountText(c: DayCounts): string {
  if (!c.weights && !c.running) return "No weights";
  return c.given ? c.done + " of " + c.given + " done" + (c.skipped ? " · " + c.skipped + " skipped" : "") : "No athletes yet";
}

export function columnName(c: string): string {
  return c === "" ? "Everyone" : c;
}
export function columnsText(cols: string[]): string {
  return cols.map(columnName).join(", ");
}

export function workoutTitle(model: Pick<WeekModel, "titles">, w: Workout): string {
  const id = norm(w.session.teamSessionID);
  return (id && model.titles[id]) || "Workout";
}

export function sessionColumns(model: Pick<WeekModel, "rows">, id: string): string[] {
  const names: string[] = [];
  for (const r of model.rows) if (norm(r.sessionID) === id && !names.includes(columnName(r.group))) names.push(columnName(r.group));
  return names;
}

export function cleanLines(lines: string[] | null | undefined): string[] {
  return (lines ?? []).map((l) => String(l).trim()).filter(Boolean);
}

export function linesPreview(lines: string[] | null | undefined): string {
  return cleanLines(lines).slice(0, 2).join(" · ");
}

export function records(workouts: Workout[]): number {
  return workouts.reduce((n, w) => n + (w.records ?? []).length, 0);
}

export function topSummary(w: Workout): string {
  if (skippedWorkout(w)) return "Skipped";
  const parts: string[] = [];
  const seen = new Set<string>();
  const blocks = liftBlocks(w.session.blocks);
  for (const b of blocks) {
    for (const e of b.exercises ?? []) {
      const key = keyOf(e);
      if (seen.has(key)) continue;
      seen.add(key);
      const ids = new Set<string>();
      for (const bb of blocks) for (const x of bb.exercises ?? []) if (keyOf(x) === key) ids.add(norm(x.id));
      const top = topSet((w.session.logs ?? []).filter((l) => ids.has(norm(l.exerciseID))));
      if (top) parts.push(e.name + " " + setText(top, e));
    }
  }
  return parts.join(" · ") || "No sets logged";
}

export interface AthleteDay {
  date: string;
  /** `skipped`: sets of the part the athlete skipped. */
  parts: { name: string; weights: boolean; logged?: boolean; state?: State; skipped?: number }[];
  state: State | null;
  records: number;
  summary: string[];
}

/** The athlete's week a day at a time: what was on it for them, and how the weights went. */
export function athleteDays(model: Pick<WeekModel, "titles">, a: Athlete): AthleteDay[] {
  const byDay = new Map<string, { date: string; parts: AthleteDay["parts"]; before: number; states: State[]; records: number; summary: string[] }>();
  const add = (date: string) => {
    let d = byDay.get(date);
    if (!d) {
      d = { date, parts: [], before: 0, states: [], records: 0, summary: [] };
      byDay.set(date, d);
    }
    return d;
  };
  for (const g of a.given) {
    const d = add(g.date);
    d.parts.push({ name: g.title, weights: true, skipped: g.skipped });
    d.states.push(g.state);
    d.records += records(g.done);
    if (g.done[0]) d.summary.push(topSummary(g.done[0]));
  }
  for (const w of a.extras) {
    const d = add(w.date);
    d.parts.push({ name: workoutTitle(model, w), weights: true });
    d.states.push("done");
    d.records += (w.records ?? []).length;
    d.summary.push(topSummary(w));
  }
  for (const t of a.track) {
    const d = add(t.date);
    const part = t.running ? { name: t.label, weights: false, state: t.state!, skipped: t.skipped } : { name: t.label, weights: false, logged: t.logged, skipped: t.skipped };
    if (t.before) d.parts.splice(d.before++, 0, part);
    else d.parts.push(part);
  }
  return [...byDay.keys()].sort().map((date) => {
    const d = byDay.get(date)!;
    const s = d.states;
    const state: State | null = s.length
      ? s.includes("missed")
        ? "missed"
        : s.every((x) => x === "skipped")
          ? "skipped"
          : s.every((x) => x === "done" || x === "skipped")
          ? "done"
          : s.includes("today")
            ? "today"
            : s.includes("done")
              ? "done"
              : "planned"
      : null;
    return { date, parts: d.parts, state, records: d.records, summary: d.summary };
  });
}

/** The weighed lifts in this week's plan, in plan order. */
export function plannedLifts(model: Pick<WeekModel, "rows">): { key: string; name: string }[] {
  const keys: { key: string; name: string }[] = [];
  for (const r of model.rows) {
    for (const b of working(r.plan?.blocks)) {
      for (const e of b.exercises ?? []) {
        if (recordOf(e) !== "weightReps") continue;
        const key = exerciseKey(e.name);
        if (key && !keys.some((k) => k.key === key)) keys.push({ key, name: e.name });
      }
    }
  }
  return keys;
}

export function initials(name: string): string {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? [parts[0]!, parts[parts.length - 1]!] : parts.slice(0, 1);
  return letters.map((w) => Array.from(w)[0] ?? "").join("").toUpperCase();
}
